import { useState, useEffect, useCallback } from "react";
import { GridPaginationModel } from "@mui/x-data-grid";
import {
  ExportDetails,
  ExportDetailsResponse,
} from "../../../../types/niffler";
import { fetchExportsHistory } from "../../../../apis/niffler/export";
import { useSnackbar } from "../../../../components/Snackbar";
import { EXPORT_CONSTANTS, EXPORT_STATUS } from "../constants";

// Map frontend enum values to backend API values
const statusToApiMapping = {
  [EXPORT_STATUS.COMPLETED]: "COMPLETED",
  [EXPORT_STATUS.IN_PROGRESS]: "IN_PROGRESS",
  [EXPORT_STATUS.FAILED]: "FAILED",
} as const;

interface UseExportHistoryParams {
  initialPageSize?: number;
  statusFilter?: EXPORT_STATUS;
}

interface UseExportHistoryResult {
  data: ExportDetails[];
  loading: boolean;
  hasError: boolean;
  totalCount: number;
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
  refetch: () => Promise<void>;
}

export function useExportHistory(
  params: UseExportHistoryParams = {}
): UseExportHistoryResult {
  const {
    initialPageSize = EXPORT_CONSTANTS.PAGINATION.DEFAULT_PAGE_SIZE,
    statusFilter,
  } = params;
  const { showError } = useSnackbar();
  const [data, setData] = useState<ExportDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setHasError(false);
      const filters = {
        page: paginationModel.page + 1, // Niffler pages start at 1
        per: paginationModel.pageSize,
        ...(statusFilter && {
          status: statusToApiMapping[statusFilter],
        }),
      };

      const response: ExportDetailsResponse =
        await fetchExportsHistory(filters);

      setData(response.data);
      setTotalCount(response.meta.total);
    } catch (err) {
      showError(
        err instanceof Error
          ? `Failed to fetch export history: ${err.message}`
          : "Failed to fetch export history"
      );
      setHasError(true);
      console.error("Failed to fetch export history", err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, statusFilter, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    hasError,
    totalCount,
    paginationModel,
    setPaginationModel,
    refetch: fetchData,
  };
}
